import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { AccountDoc } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import {
  toPipelineAccount,
  type PipelineAccount,
  type SfOpportunitiesSnapshot,
  type SfUserSnapshot,
} from '@/lib/pipelineAdapter';

interface AccountRow extends AccountDoc {
  id: string;
}

interface PipelineState {
  accounts: PipelineAccount[];
  loading: boolean;
}

/**
 * Streams all accounts and, for each one, its aggregate
 * `salesforce/opportunities` doc, then joins them into the
 * `PipelineAccount` shape the Smart Table renders. Per-account opp
 * subscriptions are added/removed reactively as the account list changes.
 */
export function useAccountsPipeline(): PipelineState {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [oppsByAccountId, setOppsByAccountId] = useState<
    Record<string, SfOpportunitiesSnapshot | null>
  >({});
  const [sfUsersById, setSfUsersById] = useState<Map<string, SfUserSnapshot>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'accounts'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: AccountRow[] = snap.docs.map((d) => {
          const data = d.data() as DocumentData;
          return { id: d.id, ...(data as AccountDoc) };
        });
        setAccounts(rows);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  // Subscribe to each account's salesforce/opportunities aggregate doc.
  // We diff the id set so we don't churn subscriptions on unrelated updates.
  useEffect(() => {
    const ids = accounts.map((a) => a.id);
    const unsubs = ids.map((id) => {
      const ref = doc(db, 'accounts', id, 'salesforce', 'opportunities');
      return onSnapshot(
        ref,
        (snap) => {
          const data = snap.exists()
            ? ((snap.data() as DocumentData) as SfOpportunitiesSnapshot)
            : null;
          setOppsByAccountId((prev) => ({ ...prev, [id]: data }));
        },
        () => {
          setOppsByAccountId((prev) => ({ ...prev, [id]: null }));
        },
      );
    });

    return () => {
      unsubs.forEach((u) => u());
    };
    // We deliberately key on the joined id list so we re-subscribe only when
    // accounts are added or removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.map((a) => a.id).join('|')]);

  // Subscribe to the small `salesforceUsers/*` collection (one doc per
  // distinct Opportunity OwnerId in the cohort) so the adapter can resolve
  // the real SF account owner for each row.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'salesforceUsers'),
      (snap) => {
        const next = new Map<string, SfUserSnapshot>();
        snap.docs.forEach((d) => {
          const data = d.data() as DocumentData;
          next.set(d.id, {
            name: typeof data.name === 'string' ? data.name : null,
            email: typeof data.email === 'string' ? data.email : null,
            isActive: !!data.isActive,
          });
        });
        setSfUsersById(next);
      },
      (err) => {
        console.error('[useAccountsPipeline] salesforceUsers stream error', err);
      },
    );
    return unsub;
  }, []);

  const caller = useMemo(
    () =>
      user
        ? {
            uid: user.uid,
            name: user.displayName ?? user.email ?? 'You',
            email: user.email ?? '',
          }
        : null,
    [user],
  );

  const pipeline = useMemo<PipelineAccount[]>(() => {
    const visible = accounts.filter(
      (row) =>
        !row.salesforceCohortStatus || row.salesforceCohortStatus === 'active',
    );
    return visible.map((row) => {
      const { id, ...accountData } = row;
      return toPipelineAccount({
        id,
        account: accountData,
        opps: oppsByAccountId[id] ?? null,
        caller,
        sfUsersById,
      });
    });
  }, [accounts, oppsByAccountId, caller, sfUsersById]);

  return { accounts: pipeline, loading };
}
